/**
 * Cost Stream Parser
 * Parses streaming cost estimation content and creates compartments
 */

class CostParser {
  constructor() {
    this.state = {
      compartments: [],
      buffer: "",
      processingState: 'idle'
    };
  }

  // =================================================================
  // PUBLIC API (used by App.js)
  // =================================================================

  processStreamContent(params) {
    const { content, section } = params;

    if (section !== "cost") {
      return {
        isSuccessful: false,
        content: []
      };
    }

    if (!content) {
      return {
        isSuccessful: false,
        content: []
      };
    }

    this.state.processingState = 'processing';
    this.state.buffer += content;

    try {
      this.parseCostCompartments(this.state.buffer);

      if (this.state.compartments.length > 0) {
        this.state.processingState = 'completed';
        return {
          isSuccessful: true,
          content: this.getValidCompartments()
        };
      }

      this.state.processingState = 'idle';
      return {
        isSuccessful: false,
        content: []
      };

    } catch (error) {
      console.error('Cost Parser: Error processing content:', error);
      this.state.processingState = 'error';
      return {
        isSuccessful: false,
        content: []
      };
    }
  }

  resetState() {
    console.log('🔄 Cost Parser: Resetting state');

    this.state = {
      compartments: [],
      buffer: "",
      processingState: 'idle'
    };
  }

  // =================================================================
  // PARSING LOGIC
  // =================================================================

  parseCostCompartments(buffer) {
    console.log('💰 Cost buffer received, length:', buffer.length);

    const beforeCount = this.state.compartments.length;
    const matches = [...buffer.matchAll(/COSTCOMPARTMENT/g)];
    const currentCount = matches.length;

    console.log(`💰 Found ${currentCount} "COSTCOMPARTMENT" patterns, had ${beforeCount} compartments before`);

    if (currentCount > beforeCount) {
      // Create new compartments only for new matches
      for (let i = beforeCount; i < currentCount; i++) {
        this.createCompartment(i + 1);
      }
      this.updateCompartmentContent(buffer);
    } else if (this.state.compartments.length > 0) {
      // Stream to last compartment
      this.streamToLastCompartment(buffer);
    }
  }

  createCompartment(number) {
    const compartment = {
      id: `cost_${number}`,
      number: number,
      title: `Cost Compartment ${number}`,
      content: "",
      isExpanded: true
    };

    this.state.compartments.push(compartment);
    console.log(`🆕 Cost Parser: Compartment ${number} created`);
  }

  extractTitle(section) {
    const lines = section.trim().split('\n');
    for (const line of lines) {
      // Check if line contains "title" (case insensitive)
      if (line.toLowerCase().includes('title')) {
        const colonIndex = line.indexOf(':');
        if (colonIndex !== -1) {
          // Extract everything after the colon and strip markdown
          const titleText = line.substring(colonIndex + 1).trim();
          return this.stripMarkdown(titleText);
        }
      }
    }
    return null;
  }

  stripMarkdown(text) {
    if (!text) return text;
    return text
      .replace(/\*\*/g, '')  // Remove bold **
      .replace(/\*/g, '')    // Remove italic *
      .replace(/__/g, '')    // Remove bold __
      .replace(/_/g, '')     // Remove italic _
      .replace(/`/g, '')     // Remove code `
      .replace(/^#+\s*/g, '') // Remove headers #
      .trim();
  }

  updateCompartmentContent(fullBuffer) {
    const sections = fullBuffer.split('COSTCOMPARTMENT');

    // Only process existing compartments, don't create new ones here
    sections.slice(1).forEach((section, index) => {
      if (index < this.state.compartments.length) {
        const existingCompartment = this.state.compartments[index];

        if (!this.isValidCompartment(existingCompartment)) {
          console.error(`❌ Invalid compartment at index ${index}:`, existingCompartment);
          return;
        }

        const title = this.extractTitle(section) || `Cost Compartment ${index + 1}`;

        // Remove the title line from content
        const lines = section.trim().split('\n');
        const contentLines = lines.filter(line => !line.toLowerCase().includes('title'));
        let cleanContent = contentLines.join('\n').trim();

        // Stop at next compartment or end markers
        const nextCompartmentIndex = cleanContent.indexOf('COSTCOMPARTMENT');
        if (nextCompartmentIndex !== -1) {
          cleanContent = cleanContent.substring(0, nextCompartmentIndex).trim();
        }

        const updatedCompartment = {
          ...existingCompartment,
          title: title,
          content: cleanContent
        };

        if (this.isValidCompartment(updatedCompartment)) {
          this.state.compartments[index] = updatedCompartment;
        }
      }
    });
  }

  streamToLastCompartment(buffer) {
    if (this.state.compartments.length === 0) return;

    const lastIndex = this.state.compartments.length - 1;
    const existingCompartment = this.state.compartments[lastIndex];

    if (!this.isValidCompartment(existingCompartment)) {
      console.error(`❌ Invalid last compartment at index ${lastIndex}:`, existingCompartment);
      return;
    }

    const sections = buffer.split('COSTCOMPARTMENT');

    if (sections.length > lastIndex + 1) {
      const lastSection = sections[lastIndex + 1];
      const title = this.extractTitle(lastSection) || existingCompartment.title;

      // Remove the title line from content
      const lines = lastSection.trim().split('\n');
      const contentLines = lines.filter(line => !line.toLowerCase().includes('title'));
      let cleanContent = contentLines.join('\n').trim();

      // Stop at next compartment or end markers
      const nextCompartmentIndex = cleanContent.indexOf('COSTCOMPARTMENT');
      if (nextCompartmentIndex !== -1) {
        cleanContent = cleanContent.substring(0, nextCompartmentIndex).trim();
      }

      const updatedCompartment = {
        ...existingCompartment,
        title: title,
        content: cleanContent
      };

      if (this.isValidCompartment(updatedCompartment)) {
        this.state.compartments[lastIndex] = updatedCompartment;
      }
    }
  }

  // =================================================================
  // UTILITY & VALIDATION
  // =================================================================

  isValidCompartment(compartment) {
    return compartment &&
           typeof compartment.id === 'string' &&
           compartment.id.length > 0 &&
           typeof compartment.number === 'number' &&
           (typeof compartment.title === 'string' || compartment.title === undefined);
  }

  getValidCompartments() {
    return this.state.compartments.filter(compartment => {
      const isValid = this.isValidCompartment(compartment);
      if (!isValid) {
        console.warn('Invalid compartment filtered out:', compartment);
      }
      return isValid;
    });
  }
}

export default CostParser;
